import React from "react";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const worldHistoryUnit9Notes: UnitNotes = {
  unitId: "unit-9",
  unitName: "Unit 9: Globalization (c. 1900–Present)",
  examWeight: "8–10%",
  focus: "How did technological advances, economic systems, and cultural exchange reshape the world into an interconnected global system?",
  sections: [
    {
      id: "topic-9-1",
      title: "Topic 9.1: Advances in Technology and Exchange",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 The "Shrinking" of the World</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Rapid advances in science and technology reduced the problem of geographic distance, accelerating interaction between people.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">📻 Communication Technologies</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Radio & Cellular Communication:</strong> Allowed instant transmission of information. Radio (early 20th century) enabled leaders to speak directly to populations (e.g., FDR's fireside chats). Cellular data (late 20th century) democratized information access.</li>
                  <li><strong>The Internet:</strong> Created a global information network, facilitating e-commerce, social media, and rapid spread of news (and misinformation).</li>
                  <li><strong>Impact:</strong> These technologies played key roles in political movements (e.g., The Arab Spring) and globalized culture.</li>
                </ul>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">✈️ Transportation Technologies</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Air Travel:</strong> Moved people and goods faster than ever before, boosting global tourism and business travel.</li>
                  <li><strong>Shipping Containers:</strong> The standardized shipping container drastically lowered transportation costs, making the global manufacturing supply chain possible.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚡ Energy Technologies</h3>
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-lg border border-yellow-200">
              <p className="text-sm text-slate-700">
                The 20th century saw a shift from coal to <strong>petroleum (oil)</strong> as the primary energy source, driving productivity but increasing pollution. <strong>Nuclear energy</strong> offered high-output alternatives but raised safety and weaponization concerns (e.g., Chernobyl, Fukushima).
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌾 The Green Revolution</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              A mid-20th-century response to global hunger, developing new agricultural techniques to increase food production.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-green-50 to-lime-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">🔬 Innovations</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Cross-breeding:</strong> Developing high-yield, disease-resistant varieties of grains (wheat, rice, corn).</li>
                  <li><strong>Genetic Engineering (GMOs):</strong> Modifying crops at the cellular level.</li>
                  <li><strong>Chemicals:</strong> Massive use of synthetic fertilizers and pesticides.</li>
                </ul>
              </div>
              <KeyTerm 
                term="Norman Borlaug" 
                definition="The 'Father of the Green Revolution' whose work saved roughly a billion people from starvation."
              />
              <div className="grid md:grid-cols-2 gap-3">
                <div className="bg-gradient-to-br from-green-100 to-green-50 p-4 rounded-lg border border-green-200">
                  <h5 className="font-bold text-green-900">✅ Positive Impacts</h5>
                  <p className="text-xs text-slate-700 mt-1">Saved roughly a billion people from starvation (especially in India, Mexico, and East Asia). Sustained a population explosion.</p>
                </div>
                <div className="bg-gradient-to-br from-red-100 to-red-50 p-4 rounded-lg border border-red-200">
                  <h5 className="font-bold text-red-900">❌ Negative Impacts</h5>
                  <p className="text-xs text-slate-700 mt-1">Environmental damage (runoff), loss of biodiversity, and marginalization of small farmers who couldn't afford new equipment/chemicals.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💊 Medical Innovations</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🧬 Antibiotics</h4>
                <p className="text-sm text-slate-700 mt-1">(e.g., Penicillin) Drastically reduced death from bacterial infections.</p>
              </div>
              <div className="bg-gradient-to-br from-purple-100 to-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">💉 Vaccines</h4>
                <p className="text-sm text-slate-700 mt-1">(e.g., Polio, Smallpox, Measles) Eradicated or managed deadly diseases, leading to increased global life expectancy.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-9-2",
      title: "Topic 9.2: Technological Advances and Limitations: Disease",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🦠 Disease and Poverty</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Diseases associated with poverty persisted in regions with poor sanitation and lack of access to clean water or healthcare (Global South).
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🦟 Malaria</h4>
                <p className="text-sm text-slate-700 mt-1">Mosquito-borne disease; continues to plague tropical regions.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🫁 Tuberculosis</h4>
                <p className="text-sm text-slate-700 mt-1">Bacterial infection impacting lungs; thrived in crowded, low-income urban areas.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">💧 Cholera</h4>
                <p className="text-sm text-slate-700 mt-1">Water-borne bacterial disease caused by sewage contamination.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Epidemics and Pandemics</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              New epidemics emerged as a result of global interconnection and human encroachment on nature.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-bold text-red-900">🦠 1918 Influenza Pandemic</h4>
                <p className="text-sm text-slate-700 mt-1">Killed 20–50 million people globally; spread by soldiers returning from WWI.</p>
              </div>
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-4 rounded-lg border-l-4 border-pink-400">
                <h4 className="font-bold text-pink-900">🩸 HIV/AIDS</h4>
                <p className="text-sm text-slate-700 mt-1">Emerged in the 1980s; decimated populations in Sub-Saharan Africa and marginalized communities in the West. Advances in antiretroviral drugs have turned it into a manageable chronic condition in wealthy nations.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-orange-900">🦠 Ebola</h4>
                <p className="text-sm text-slate-700 mt-1">Viral hemorrhagic fever in West Africa; high mortality rate caused global alarm.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">😷 COVID-19</h4>
                <p className="text-sm text-slate-700 mt-1">Demonstrated the vulnerability of the globalized supply chain and economy to viral outbreaks.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👴 Diseases of Longevity</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              As life expectancy increased due to better hygiene and medicine, diseases associated with aging and lifestyle increased.
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-amber-100 to-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">❤️ Heart Disease</h4>
                <p className="text-sm text-slate-700 mt-1">Linked to sedentary lifestyles and processed foods.</p>
              </div>
              <div className="bg-gradient-to-br from-purple-100 to-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🧠 Alzheimer's Disease</h4>
                <p className="text-sm text-slate-700 mt-1">A form of dementia affecting aging populations.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-9-3",
      title: "Topic 9.3: Technological Advances: Environment",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Humans Changing the Environment (The Anthropocene)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Human activity became the dominant influence on climate and the environment.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">🌳 Deforestation</h4>
                <p className="text-sm text-slate-700 mt-1">Clearing forests for agriculture (palm oil, beef) or urbanization (Amazon Rainforest).</p>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🏜️ Desertification</h4>
                <p className="text-sm text-slate-700 mt-1">Fertile land becoming desert due to drought, deforestation, or inappropriate agriculture (Sahel region).</p>
              </div>
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-bold text-gray-900">💨 Air Quality</h4>
                <p className="text-sm text-slate-700 mt-1">Industrial pollution and vehicle emissions reduced air quality (smog in Beijing, Delhi).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ Competition for Resources</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">💧 Fresh Water</h4>
                <p className="text-sm text-slate-700 mt-1">Population growth and industrial use strained water supplies.</p>
              </div>
              <div className="bg-gradient-to-br from-orange-100 to-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🛢️ Fossil Fuels</h4>
                <p className="text-sm text-slate-700 mt-1">Competition for oil and gas drove geopolitical conflict (Middle East conflicts).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌡️ Climate Change</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🔥 Causes</h4>
                <p className="text-sm text-slate-700 mt-1">Release of greenhouse gases (CO₂, Methane) from burning fossil fuels and industrial agriculture.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">⚡ Effects</h4>
                <p className="text-sm text-slate-700 mt-1">Rising sea levels, extreme weather events, melting ice caps.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🗣️ Debates</h4>
                <p className="text-sm text-slate-700 mt-1">While scientific consensus confirms human-caused climate change, political debates continue regarding the balance between economic growth and environmental regulation (e.g., Kyoto Protocol, Paris Agreement).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-9-4",
      title: "Topic 9.4: Economics in the Global Age",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💰 Economic Liberalization (Neoliberalism)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Following the end of the Cold War, most of the world adopted free-market principles.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">📋 Neoliberal Policies</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li>Cutting taxes</li>
                  <li>Reducing tariffs (free trade)</li>
                  <li>Privatizing state-owned industries</li>
                  <li>Deregulation</li>
                </ul>
              </div>
              <KeyTerm 
                term="Key Leaders" 
                definition="Ronald Reagan (USA), Margaret Thatcher (UK), Augusto Pinochet (Chile), Deng Xiaoping (China)."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌐 Shifting Economies</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">💻 Knowledge Economies</h4>
                <p className="text-sm text-slate-700 mt-1">Wealthy nations (US, Finland, Japan) shifted toward information, design, and technology rather than manufacturing.</p>
              </div>
              <div className="bg-gradient-to-br from-orange-100 to-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🏭 Manufacturing Economies</h4>
                <p className="text-sm text-slate-700 mt-1">Industrial production moved to Asia and Latin America (Vietnam, Bangladesh, Mexico) due to lower labor costs and fewer regulations.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏢 Multinational Corporations (MNCs)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Companies that operate in multiple countries, often challenging the power of national governments.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🍫 Nestlé</h4>
                <p className="text-sm text-slate-700 mt-1">Swiss-based food/drink company (controversies over water rights).</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🚗 Nissan</h4>
                <p className="text-sm text-slate-700 mt-1">Japanese auto manufacturer with global factories.</p>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🏭 Mahindra & Mahindra</h4>
                <p className="text-sm text-slate-700 mt-1">Indian conglomerate.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🤝 Regional Trade Agreements</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Nations formed blocs to reduce tariffs and encourage trade.
            </p>
            <div className="space-y-3">
              <KeyTerm 
                term="NAFTA (now USMCA)" 
                definition="USA, Canada, Mexico."
              />
              <KeyTerm 
                term="ASEAN" 
                definition="Southeast Asian nations."
              />
              <KeyTerm 
                term="EU (European Union)" 
                definition="Created a single market and currency (Euro) in Europe."
              />
              <KeyTerm 
                term="WTO (World Trade Organization)" 
                definition="Global body regulating trade rules."
              />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-9-5",
      title: "Topic 9.5: Calls for Reform and Responses",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Rights-Based Discourses</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Old assumptions about race, class, gender, and religion were challenged.
            </p>
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
              <h4 className="font-bold text-purple-900">📜 UN Universal Declaration of Human Rights (1948)</h4>
              <p className="text-sm text-slate-700 mt-1">Established basic rights for all humans (freedom of speech, religion, freedom from slavery).</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👥 Expanded Access and Inclusivity</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-4 rounded-lg border-l-4 border-pink-400">
                <h4 className="font-bold text-pink-900">👩 Women's Rights</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Suffrage:</strong> Gained in most nations by mid-20th century (US 1920, Brazil 1932, Turkey 1934).</li>
                  <li><strong>Education/Literacy:</strong> Rates for women rose globally.</li>
                  <li><strong>CEDAW:</strong> UN convention against discrimination against women.</li>
                </ul>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">🤝 Civil Rights</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>US Civil Rights Act of 1965:</strong> Ended legal segregation (Jim Crow).</li>
                  <li><strong>End of Apartheid:</strong> South Africa (1990s) ended racial segregation; Nelson Mandela elected president.</li>
                  <li><strong>Caste Reservation System:</strong> India created quotas for "scheduled castes" (Dalits) in education and government.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌱 Environmental Movements</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">🌍 Greenpeace</h4>
                <p className="text-sm text-slate-700 mt-1">Global NGO focused on battling deforestation, whaling, and nuclear testing.</p>
              </div>
              <div className="bg-gradient-to-r from-lime-50 to-green-50 p-4 rounded-lg border border-lime-200">
                <h4 className="font-bold text-lime-900">🌳 Green Belt Movement</h4>
                <p className="text-sm text-slate-700 mt-1">Founded by Wangari Maathai in Kenya; focused on tree planting and women's empowerment.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⛪ Religious & Cultural Reform</h3>
            <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
              <h4 className="font-bold text-orange-900">✝️ Liberation Theology</h4>
              <p className="text-sm text-slate-700 mt-1">Catholic movement in Latin America combining theology with socialist principles to help the poor.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-9-6",
      title: "Topic 9.6: Globalized Culture",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎨 Political and Social Changes in Arts</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-purple-100 to-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🎭 Cubism (Picasso)</h4>
                <p className="text-sm text-slate-700 mt-1">Challenged realistic representation.</p>
              </div>
              <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🎵 Harlem Renaissance</h4>
                <p className="text-sm text-slate-700 mt-1">Jazz and literature celebrating Black culture.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Influence of Globalized Society</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Culture became less local and more global/consumerist.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🎶 Reggae</h4>
                <p className="text-sm text-slate-700 mt-1">Jamaican music (Bob Marley) became a global symbol of resistance.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🎬 Bollywood</h4>
                <p className="text-sm text-slate-700 mt-1">Indian film industry gained global popularity.</p>
              </div>
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-900">🎤 K-Pop</h4>
                <p className="text-sm text-slate-700 mt-1">Korean pop music (late 20th/early 21st century) demonstrated non-Western cultural dominance.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">⚽ Sports</h4>
                <p className="text-sm text-slate-700 mt-1">The Olympics and FIFA World Cup brought nations together in shared entertainment.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛍️ Global Consumerism</h3>
            <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
              <p className="text-sm text-slate-700">
                Global brands (Coca-Cola, McDonald's, Toyota, Nike) created a somewhat homogenized global culture ("Americanization" or "Westernization"). Online commerce (Amazon, Alibaba/eBay) transcended borders.
              </p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-9-7",
      title: "Topic 9.7: Resistance to Globalization",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💪 Responses to Economic Globalization</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Not everyone benefited from the new global economy. Critics argued it exploited workers in the Global South and destroyed the environment.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🏦 Anti-IMF/World Bank Activism</h4>
                <p className="text-sm text-slate-700 mt-1">Protests against the conditions these organizations put on loans to poor countries (austerity measures).</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">✊ Battle of Seattle (1999)</h4>
                <p className="text-sm text-slate-700 mt-1">Massive protests against the WTO.</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">☕ Fair Trade Movement</h4>
                <p className="text-sm text-slate-700 mt-1">Sought to ensure farmers in developing nations received fair prices for coffee, chocolate, etc.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛡️ Resistance to Cultural Globalization</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Some states and groups resisted the intrusion of Western/global culture.
            </p>
            <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
              <h4 className="font-bold text-red-900">🇨🇳 China (Weibo)</h4>
              <p className="text-sm text-slate-700 mt-1">The Chinese government created a "Great Firewall" to control the internet and developed its own social media platforms (Weibo, WeChat) to block Western influence (Facebook/Twitter).</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-9-8",
      title: "Topic 9.8: Institutions Developing in a Globalized World",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🕊️ International Organizations for Peace</h3>
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-bold text-blue-900">🇺🇳 United Nations (UN)</h4>
              <p className="text-sm text-slate-700 mt-1">Formed after WWII to prevent future conflicts and facilitate cooperation. Includes the Security Council (peacekeeping) and agencies like UNICEF and UNESCO.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🤝 Nongovernmental Organizations (NGOs)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Organizations independent of governments that address global problems.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🏥 International Committee of the Red Cross</h4>
                <p className="text-sm text-slate-700 mt-1">Humanitarian aid in war zones.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">✊ Amnesty International</h4>
                <p className="text-sm text-slate-700 mt-1">Focuses on human rights violations.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">⚕️ Doctors Without Borders</h4>
                <p className="text-sm text-slate-700 mt-1">Medical aid in crisis zones.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💰 Economic Institutions</h3>
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-bold text-green-900">💳 Microfinance</h4>
              <p className="text-sm text-slate-700 mt-1">Providing very small loans to people in developing nations (especially women) to start businesses.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Key Example:</strong> Grameen Bank in Bangladesh (Muhammad Yunus).</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-9-9",
      title: "Topic 9.9: Continuity and Change in a Globalized World",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📈 Changes (1900–Present)</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🔬 Science/Technology</h4>
                <p className="text-sm text-slate-700 mt-1">Fundamental alteration of how humans communicate (internet), travel (planes), and survive (antibiotics/Green Revolution).</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">💰 Economy</h4>
                <p className="text-sm text-slate-700 mt-1">Shift from mercantilism/imperialism to free-market global capitalism and multinational corporations.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🏛️ Political</h4>
                <p className="text-sm text-slate-700 mt-1">Fall of land-based empires; rise of nation-states; rise of international organizations (UN, EU).</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">👥 Society</h4>
                <p className="text-sm text-slate-700 mt-1">Increased rights for women; challenges to racial hierarchy.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 Continuities (1900–Present)</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">📊 Inequality</h4>
                <p className="text-sm text-slate-700 mt-1">While global wealth increased, the gap between the Global North (wealthy) and Global South (developing) persisted.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">⚔️ Conflict</h4>
                <p className="text-sm text-slate-700 mt-1">Despite the UN, regional wars, ethnic conflicts, and acts of terrorism continued.</p>
              </div>
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-900">👩 Patriarchy</h4>
                <p className="text-sm text-slate-700 mt-1">Despite gains, women continued to face barriers in political and economic equality globally.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "key-vocabulary",
      title: "Key Vocabulary for Unit 9",
      content: (
        <div className="space-y-3">
          <KeyTerm 
            term="Anthropocene" 
            definition="The current geological age, viewed as the period during which human activity has been the dominant influence on climate and the environment."
          />
          <KeyTerm 
            term="Asian Tigers" 
            definition="Hong Kong, Singapore, South Korea, and Taiwan—nations that industrialized rapidly in the late 20th century."
          />
          <KeyTerm 
            term="Knowledge Economy" 
            definition="An economy where growth is dependent on the quantity, quality, and accessibility of the information available, rather than the means of production."
          />
          <KeyTerm 
            term="Neoliberalism" 
            definition="A modified form of liberalism tending to favor free-market capitalism and deregulation."
          />
          <KeyTerm 
            term="Green Revolution" 
            definition="The great increase in production of food grains (such as rice and wheat) due to the introduction of high-yielding varieties, to the use of pesticides, and to better management techniques."
          />
          <KeyTerm 
            term="Universal Declaration of Human Rights" 
            definition="A 1948 statement in which the United Nations declared that all human beings have rights to life, liberty, and security."
          />
        </div>
      )
    }
  ]
};

export default worldHistoryUnit9Notes;